import React, { useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Braces } from 'lucide-react';
import RichEmailEditor, { RichEmailEditorHandle } from '@/components/email/editor/RichEmailEditor';
import { textToHtml } from '@/utils/emailHtml';
import { useTemplatePlaceholders } from '@/hooks/email/useEmailTemplates';

/** Fallback list used until the backend placeholder catalog resolves. */
const FALLBACK_PLACEHOLDERS = [
  'first_name',
  'last_name',
  'full_name',
  'company_name',
  'position',
  'email',
  'phone',
  'my_first_name',
  'my_last_name',
  'my_full_name',
  'my_email',
];

const escapeRe = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Escape angle-bracket placeholders so the editor/parser keeps them as text. */
const escapePlaceholders = (html: string, tokens: string[]): string =>
  html.replace(
    new RegExp(`<(${tokens.map(escapeRe).join('|')})>`, 'gi'),
    (_m, token) => `&lt;${token}&gt;`,
  );

/** Restore escaped placeholders to their raw angle-bracket form for saving. */
const unescapePlaceholders = (html: string, tokens: string[]): string =>
  html.replace(
    new RegExp(`&lt;(${tokens.map(escapeRe).join('|')})&gt;`, 'gi'),
    (_m, token) => `<${token}>`,
  );

const looksLikeHtml = (value: string, tokens: string[]): boolean =>
  /<\/?[a-z][^>]*>/i.test(unescapePlaceholders(value, tokens));

const toEditorHtml = (value: string, tokens: string[]): string => {
  if (!value) return '';
  const base = looksLikeHtml(value, tokens) ? value : textToHtml(value);
  return escapePlaceholders(base, tokens);
};

const SOURCE_LABELS: Record<string, string> = {
  recipient: 'Recipient (lead / contact)',
  sender: 'Sender (you)',
};

interface TemplateBodyEditorProps {
  value: string;
  onChange: (html: string) => void;
}

const TemplateBodyEditor: React.FC<TemplateBodyEditorProps> = ({ value, onChange }) => {
  const editorRef = useRef<RichEmailEditorHandle>(null);
  const { placeholders } = useTemplatePlaceholders();

  /** All recognised tokens (API catalog fields + aliases), longest first. */
  const tokens = useMemo(() => {
    const set = new Set<string>(FALLBACK_PLACEHOLDERS);
    placeholders.forEach((p) => {
      if (p.field) set.add(p.field);
      (p.tokens || []).forEach((t) => {
        if (t && !/\s/.test(t)) set.add(t);
      });
    });
    return Array.from(set).sort((a, b) => b.length - a.length);
  }, [placeholders]);

  /** Grouped menu entries: API catalog when available, fallback otherwise. */
  const groups = useMemo(() => {
    const items = placeholders.length
      ? placeholders.map((p) => ({
          field: p.field,
          description: p.description,
          source: p.source || 'recipient',
        }))
      : FALLBACK_PLACEHOLDERS.map((field) => ({
          field,
          description: undefined as string | undefined,
          source: field.startsWith('my_') ? 'sender' : 'recipient',
        }));

    const bySource = new Map<string, typeof items>();
    items.forEach((item) => {
      const list = bySource.get(item.source) || [];
      list.push(item);
      bySource.set(item.source, list);
    });
    return Array.from(bySource.entries());
  }, [placeholders]);

  const editorValue = useMemo(() => toEditorHtml(value, tokens), [value, tokens]);

  const handleEditorChange = (html: string) => {
    onChange(unescapePlaceholders(html, tokens));
  };

  const placeholderMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs">
          <Braces className="h-3.5 w-3.5 mr-1.5" />
          Placeholder
          <ChevronDown className="h-3.5 w-3.5 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 max-h-80 overflow-y-auto">
        {groups.map(([source, items], idx) => (
          <React.Fragment key={source}>
            {idx > 0 && <DropdownMenuSeparator />}
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {SOURCE_LABELS[source] || source}
            </DropdownMenuLabel>
            {items.map((item) => (
              <DropdownMenuItem
                key={`${source}-${item.field}`}
                onClick={() => editorRef.current?.insertHtml(`&lt;${item.field}&gt;`)}
                className="flex flex-col items-start gap-0.5"
              >
                <span className="text-xs font-mono">{`<${item.field}>`}</span>
                {item.description && (
                  <span className="text-[11px] text-muted-foreground leading-snug">
                    {item.description}
                  </span>
                )}
              </DropdownMenuItem>
            ))}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <RichEmailEditor
      ref={editorRef}
      value={editorValue}
      onChange={handleEditorChange}
      minHeight="260px"
      maxHeight="45vh"
      placeholder="Write your template body…"
      toolbarExtras={placeholderMenu}
    />
  );
};

export default TemplateBodyEditor;
